import { FC } from "react";
import { useController } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import PropTypes from "prop-types";
import { DatePicker, Typography } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import "../CBShared.style.scss";

interface CBDatePickerTypes {
  label?: string;
  name: string;
  control: any;
  required?: boolean;
  disabled?: boolean;
  tooltipMessage?: string;
  helperLink?: string;
  caption?: string;
  onBlur?: any;
  onChange?: any;
  range?: boolean;
  className?: string;
  showTime?: boolean;
  format?: string;
  minDate?: string;
}

const CBDatePicker: FC<CBDatePickerTypes> = ({
  label,
  name,
  control,
  required = false,
  disabled = false,
  tooltipMessage = "",
  helperLink = "",
  caption = "",
  onBlur = () => {},
  onChange = () => {},
  range,
  className,
  showTime = false,
  format = "MM/DD/YYYY",
  minDate,
}) => {
  const { field, fieldState } = useController({ name, control });
  const id = `datePicker-${field.name}`;
  const { Text } = Typography;
  const { RangePicker } = DatePicker;

  return (
    <div className={`CBInput my-2.5 text-left ${className}`}>
      <div className="container">
        <label style={{ display: "flex" }} htmlFor={id}>
          {label && (
            <div className="flex gap-1">
              <Text
                style={{ marginBottom: 0, whiteSpace: "nowrap" }}
              >{`${label}`}</Text>
              <Text
                type="danger"
                style={{ marginBottom: 0 }}
              >{`${required ? " *" : ""}`}</Text>
            </div>
          )}
          {helperLink && (
            <a
              style={{ textDecoration: "none" }}
              href={helperLink}
              rel="noreferrer"
              target="_blank"
            >
              (Help)
            </a>
          )}
          {tooltipMessage && (
            <>
              <span
                data-tooltip-id={`${id}-tooltip`}
                data-tooltip-content={tooltipMessage}
                className="tooltip"
              >
                <FaQuestionCircle />
              </span>
              <Tooltip id={`${id}-tooltip`} />
            </>
          )}
        </label>
      </div>
      {!range ? (
        <DatePicker
          id={id}
          style={{ width: "-webkit-fill-available" }}
          disabled={disabled}
          ref={field.ref}
          name={name}
          size="large"
          allowClear={false}
          value={field.value ? dayjs(field.value) : ""}
          showTime={showTime}
          minDate={dayjs(minDate)}
          onBlur={(e: any) => {
            field.onBlur();
            onBlur(moment(e.$d).format(format));
          }}
          onChange={(e: any) => {
            field.onChange(moment(e.$d).format(format));
            onChange(moment(e.$d).format(format));
          }}
          format={format}
        />
      ) : (
        <RangePicker
          size="large"
          id={id}
          style={{ width: "-webkit-fill-available" }}
          disabled={disabled}
          ref={field.ref}
          name={name}
          allowClear={true}
          onBlur={() => {
            field.onBlur();
          }}
          onChange={(e: any) => {
            field.onChange([
              moment(e ? e[0]?.$d : "").format("MM/DD/YYYY"),
              moment(e ? e[1]?.$d : "").format("MM/DD/YYYY"),
            ]);
            onChange([
              moment(e ? e[0]?.$d : "").format("MM/DD/YYYY"),
              moment(e ? e[1]?.$d : "").format("MM/DD/YYYY"),
            ]);
          }}
          format={"MM/DD/YYYY"}
        />
      )}
      {caption && <Text type="secondary">{caption}</Text>}
      {!!fieldState.error && (
        <span role="alert" className="text-red-500">
          {fieldState.error.message}
        </span>
      )}
    </div>
  );
};

CBDatePicker.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltipMessage: PropTypes.string,
  helperLink: PropTypes.string,
  caption: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
};

export default CBDatePicker;
